/**
 * Readiness Page
 *
 * Context-aware item readiness dashboard.
 *
 * Left panel:  context picker (cycle → track) + item list with readiness pass/fail
 * Right panel: ValuesTab for the selected item (to address failing criteria inline)
 *
 * The effective readiness definition is resolved via:
 *   Track ?? Cycle ?? Workspace (workspaces.readiness_definition_id)
 * and every item is also checked against the workspace's every-item Hard Stops
 * (lib/itemReadiness.ts evaluateReadyForRanking), with or without a definition.
 *
 * Picking a Cycle or Track also narrows the list to the items in its Item Pool(s)
 * (the same membership the Items list and the Track page use).
 */

import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Search,
  ChevronRight,
  ExternalLink,
  Info,
  ChevronDown,
} from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { ValuesTab } from '../../components/prioritization/item-view/activity/ValuesTab';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useAttributesStore } from '../../stores/prioritization/attributesStore';
import { useValueProposalStore } from '../../stores/prioritization/valueProposalStore';
import { isOfficialValue } from '../../lib/valueScope';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useTrackStore } from '../../stores/prioritization/trackStore';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { useReadinessDefinitionStore } from '../../stores/prioritization/readinessDefinitionStore';
import { evaluateReadyForRanking } from '../../lib/itemReadiness';
import { useAgentSignoffs } from '../../hooks/useAgentSignoffs';
import { useDefinitionPolicyStore } from '../../stores/prioritization/definitionPolicyStore';
import { findPoolOnTrack } from '../../lib/poolScope';
import type { Item } from '../../types/prioritization/lifecycle';
import type { ReadinessCriterion } from '../../types/prioritization/readinessDefinition';

// ── Source badge ──────────────────────────────────────────────────────────────

const SOURCE_LABELS: Record<string, { label: string; class: string }> = {
  workspace: { label: 'Workspace default', class: 'bg-[var(--hover-tint)] text-[var(--text-sec)]' },
  cycle: { label: 'Inherited from Cycle', class: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  track: { label: 'Set by Track', class: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  session: { label: 'Set by Session', class: 'bg-[var(--slate-dim)] text-[var(--text-sec)]' },
  none: { label: 'No definition', class: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
};

function SourceBadge({ source }: { source: string }) {
  const meta = SOURCE_LABELS[source] ?? SOURCE_LABELS.none;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${meta.class}`}>
      {meta.label}
    </span>
  );
}

// ── Readiness pill ────────────────────────────────────────────────────────────

function ReadinessPill({ pass, passCount, total }: { pass: boolean; passCount: number; total: number }) {
  // Nothing to check (no definition, every-item rules all met) is ready, not "no criteria".
  if (pass) {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Ready
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs font-medium text-[var(--coral)]">
      <XCircle className="h-3.5 w-3.5" />
      {passCount}/{total}
    </span>
  );
}

// ── Criterion result row ──────────────────────────────────────────────────────

const REMEDIATION_TYPE_LABELS: Record<string, string> = {
  directional_planning: 'Directional Planning',
  detailed_planning: 'Detailed Planning',
  configuration: 'Configuration',
  governance: 'Governance',
};

function CriterionResultRow({
  criterion,
  pass,
  reason,
  remediationRole,
  remediationType,
  everyItem,
}: {
  criterion: ReadinessCriterion;
  pass: boolean;
  reason?: string;
  remediationRole?: string;
  remediationType?: string;
  everyItem?: boolean;
}) {
  let label = '';
  if (criterion.type === 'field_populated') label = criterion.label ?? criterion.fieldKey;
  if (criterion.type === 'attribute_values') label = criterion.label ?? criterion.attributeKey;
  if (criterion.type === 'agent_signoff') label = criterion.label ?? criterion.agentKey;

  return (
    <div className={`flex items-start gap-2  px-3 py-2 text-sm ${pass ? 'bg-[var(--green-dim)]' : 'bg-[var(--coral-dim)]'}`}>
      {pass ? (
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--green)]" />
      ) : (
        <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[var(--coral)]" />
      )}
      <div className="flex-1 min-w-0">
        <p className={`font-medium ${pass ? 'text-[var(--green)] dark:text-[var(--text-ter)]' : 'text-[var(--coral)]'}`}>
          {label}
          {everyItem && <span className="ml-2 text-xs font-normal text-[var(--text-ter)]">every item</span>}
        </p>
        {!pass && reason && <p className="mt-0.5 text-xs text-[var(--coral)]">{reason}</p>}
        {!pass && (remediationRole || remediationType) && (
          <div className="mt-1 flex flex-wrap gap-1.5">
            {remediationRole && (
              <span className="inline-flex items-center rounded-full border border-[var(--coral)] bg-[var(--bg-raised)] px-2 py-0.5 text-xs text-[var(--coral)]">
                Fix: {remediationRole}
              </span>
            )}
            {remediationType && (
              <span
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${
                  remediationType === 'directional_planning'
                    ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                    : remediationType === 'governance'
                    ? 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)]'
                    : 'border-[var(--rule)] bg-[var(--surface)] text-[var(--text-sec)]'
                }`}
              >
                {REMEDIATION_TYPE_LABELS[remediationType] ?? remediationType}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function ReadinessPage() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);
  // Readiness conditions are set in Settings; offer the edit only to people who may make it.
  const canEditConditions = useUserStore((s) => s.can('admin.settings'));

  // Items + values
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const attributes = useAttributesStore((s) => s.attributes);
  const fetchAttributes = useAttributesStore((s) => s.fetchFromSupabase);
  const acceptedValues = useValueProposalStore((s) => s.acceptedValues);
  const fetchProposals = useValueProposalStore((s) => s.fetchFromSupabase);

  // Cycles + tracks (for context picker)
  const cycles = useDegapProcessStore((s) => s.cycles);
  const fetchCycles = useDegapProcessStore((s) => s.fetchFromSupabase);
  const tracks = useTrackStore((s) => s.tracks);
  const fetchTracksForCycle = useTrackStore((s) => s.fetchTracksForCycle);

  // Item pools (which items belong to the picked cycle / track)
  const itemPools = useItemPoolsStore((s) => s.itemPools);
  const fetchItemPools = useItemPoolsStore((s) => s.fetchFromSupabase);

  // Readiness definitions
  const definitions = useReadinessDefinitionStore((s) => s.definitions);
  const definitionsLoading = useReadinessDefinitionStore((s) => s.loading);
  const definitionsWorkspaceId = useReadinessDefinitionStore((s) => s.workspaceId);
  const loadForWorkspace = useReadinessDefinitionStore((s) => s.loadForWorkspace);
  const getEffectiveDefinition = useReadinessDefinitionStore((s) => s.getEffectiveDefinition);
  const workspaceDefinitionId = useReadinessDefinitionStore((s) => s.workspaceDefinitionId);
  const everyItemRules = useDefinitionPolicyStore((s) => s.overrides);

  // Context selection
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCriteriaDetail, setShowCriteriaDetail] = useState(true);

  useEffect(() => {
    fetchItems();
    fetchAttributes();
    fetchProposals();
    fetchCycles();
    fetchItemPools();
    if (workspaceId) loadForWorkspace(workspaceId);
  }, [fetchItems, fetchAttributes, fetchProposals, fetchCycles, fetchItemPools, workspaceId, loadForWorkspace]);

  useEffect(() => {
    if (selectedCycleId) fetchTracksForCycle(selectedCycleId);
  }, [selectedCycleId, fetchTracksForCycle]);

  // Reset track when cycle changes
  useEffect(() => {
    setSelectedTrackId(null);
  }, [selectedCycleId]);

  // Selected cycle / track objects
  const selectedCycle = useMemo(
    () => cycles.find((c) => c.id === selectedCycleId) ?? null,
    [cycles, selectedCycleId]
  );
  const selectedTrack = useMemo(
    () => tracks.find((t) => t.id === selectedTrackId) ?? null,
    [tracks, selectedTrackId]
  );

  // Tracks for the selected cycle
  const cycleTrackOptions = useMemo(
    () => tracks.filter((t) => (t as any).cycleId === selectedCycleId || (t as any).cycle_id === selectedCycleId),
    [tracks, selectedCycleId]
  );

  // Effective definition via inheritance
  const { definition: effectiveDefinition, source } = useMemo(() => {
    // Pull the readiness definition id from each scope object (may be null).
    // Cycles and tracks both reach the page camelCased (fetchAll / trackRowToDomain).
    // The workspace layer is workspaces.readiness_definition_id.
    void definitions;
    return getEffectiveDefinition({
      workspaceDefinitionId,
      cycleDefinitionId: selectedCycle?.readinessDefinitionId ?? null,
      trackDefinitionId: selectedTrack?.readinessDefinitionId ?? null,
    });
  }, [getEffectiveDefinition, definitions, workspaceDefinitionId, selectedCycle, selectedTrack]);

  // Items that belong to the picked Track (its pool) or Cycle (pools in the cycle).
  // null = nothing picked, so every active item is in scope.
  const scopeItemIds = useMemo(() => {
    if (selectedTrackId) {
      return new Set(findPoolOnTrack(itemPools, selectedTrackId)?.priorityIds ?? []);
    }
    if (selectedCycleId) {
      const ids = new Set<string>();
      for (const pool of itemPools) {
        if (pool.cycleId === selectedCycleId) pool.priorityIds?.forEach((id) => ids.add(id));
      }
      return ids;
    }
    return null;
  }, [itemPools, selectedCycleId, selectedTrackId]);

  // Active items, narrowed to the picked Cycle / Track
  const allActiveCount = useMemo(
    () => items.filter((i) => i.exit_status === 'active').length,
    [items]
  );
  const activeItems: Item[] = useMemo(
    () =>
      items.filter(
        (i) => i.exit_status === 'active' && (!scopeItemIds || scopeItemIds.has(i.id))
      ),
    [items, scopeItemIds]
  );

  // Build accepted values map: itemId → attributeKey → value.
  // Item readiness reads the official value only — a session's value never
  // stands in for it (roadmap 1C, V-3).
  const acceptedValuesMap = useMemo(() => {
    const m: Record<string, Record<string, unknown>> = {};
    for (const av of acceptedValues) {
      if (!isOfficialValue(av)) continue;
      if (!m[av.itemId]) m[av.itemId] = {};
      m[av.itemId][av.attributeKey] = (av as any).value ?? (av as any).proposedValue;
    }
    return m;
  }, [acceptedValues]);

  // Evaluate every item: the every-item Hard Stops always, plus the effective
  // definition when one is in force.
  const agentSignoffs = useAgentSignoffs(useMemo(() => activeItems.map((i) => i.id), [activeItems]));
  const evalResults = useMemo(() => {
    void everyItemRules;
    const results: Record<string, ReturnType<typeof evaluateReadyForRanking>> = {};
    for (const item of activeItems) {
      results[item.id] = evaluateReadyForRanking(item, effectiveDefinition, acceptedValuesMap[item.id] ?? {}, agentSignoffs[item.id] ?? {});
    }
    return results;
  }, [activeItems, effectiveDefinition, acceptedValuesMap, everyItemRules, agentSignoffs]);

  // Filter + sort items: failing first, then passing
  const displayItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? activeItems.filter(
          (i) => i.title?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q)
        )
      : activeItems;

    return [...filtered].sort((a, b) => {
      const aPass = evalResults[a.id]?.pass ?? true;
      const bPass = evalResults[b.id]?.pass ?? true;
      if (aPass === bPass) return 0;
      return aPass ? 1 : -1; // failing first
    });
  }, [activeItems, searchQuery, evalResults]);

  // Auto-select first item (also when the picked item drops out of a new Cycle / Track)
  useEffect(() => {
    const stillInScope = selectedItemId !== null && activeItems.some((i) => i.id === selectedItemId);
    if (!stillInScope && displayItems.length > 0) {
      setSelectedItemId(displayItems[0].id);
    }
  }, [displayItems, activeItems, selectedItemId]);

  const selectedItem = useMemo(
    () => activeItems.find((i) => i.id === selectedItemId) ?? null,
    [activeItems, selectedItemId]
  );

  const selectedItemResult = selectedItemId ? evalResults[selectedItemId] : undefined;

  // Summary counts
  const { readyCount, notReadyCount } = useMemo(() => {
    let r = 0;
    let n = 0;
    for (const item of activeItems) {
      const res = evalResults[item.id];
      if (!res) continue;
      if (res.pass) r++;
      else n++;
    }
    return { readyCount: r, notReadyCount: n };
  }, [activeItems, evalResults]);

  // ── No definitions configured ─────────────────────────────────────────────

  // Only once the workspace's definitions have actually been fetched (not before).
  const definitionsLoaded = Boolean(workspaceId) && !definitionsLoading && definitionsWorkspaceId === workspaceId;

  // With no definition anywhere there is nothing to check items against — but
  // the list, the Cycle / Track narrowing and the values panel still work, so
  // the page says so in a strip instead of standing in front of them.
  const noDefinitions = definitions.length === 0 && definitionsLoaded;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[var(--rule)] bg-[var(--bg-raised)] px-6 py-4">
        <PageHeader
          title="Readiness"
          subtitle="Is each item ready to be ranked here? Every item is checked against the workspace's every-item Hard Stops, plus the readiness rules for the cycle or track you pick."
          icon={<CheckCircle2 className="h-5 w-5 text-[var(--green)]" />}
        />

        {/* Context picker */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-[var(--text-sec)]">Cycle</label>
            <div className="relative">
              <select
                value={selectedCycleId ?? ''}
                onChange={(e) => setSelectedCycleId(e.target.value || null)}
                className="appearance-none border border-[var(--rule)] bg-[var(--bg-raised)] py-1.5 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All items (workspace)</option>
                {cycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name ?? c.title ?? c.id}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-ter)]" />
            </div>
          </div>

          {selectedCycleId && cycleTrackOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              <label className="text-xs font-medium text-[var(--text-sec)]">Track</label>
              <div className="relative">
                <select
                  value={selectedTrackId ?? ''}
                  onChange={(e) => setSelectedTrackId(e.target.value || null)}
                  className="appearance-none border border-[var(--rule)] bg-[var(--bg-raised)] py-1.5 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Any Track (cycle level)</option>
                  {cycleTrackOptions.map((t) => (
                    <option key={t.id} value={t.id}>
                      {(t as any).name ?? t.id}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-ter)]" />
              </div>
            </div>
          )}

          {/* Effective definition */}
          <div className="ml-auto flex items-center gap-2">
            {effectiveDefinition ? (
              <>
                <SourceBadge source={source} />
                <span className="text-xs font-medium text-[var(--text-sec)]">{effectiveDefinition.name}</span>
                <span className="text-xs text-[var(--text-ter)]">
                  ({effectiveDefinition.criteria.length} criteria)
                </span>
                {canEditConditions ? (
                  <Link
                    to={`/prioritization/settings/readiness?definition=${encodeURIComponent(effectiveDefinition.id)}`}
                    className="text-xs font-medium text-[var(--text-pri)] underline"
                  >
                    Edit conditions
                  </Link>
                ) : (
                  <span className="text-xs text-[var(--text-ter)]">
                    Changing these conditions needs the settings permission.
                  </span>
                )}
              </>
            ) : (
              <>
                <SourceBadge source="none" />
                <Link
                  to="/prioritization/settings/readiness"
                  className="text-xs text-[var(--text-ter)] hover:underline"
                >
                  Configure
                </Link>
              </>
            )}
          </div>
        </div>

        {/* No definition anywhere: say so, and keep the list */}
        {noDefinitions && (
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 border border-[var(--rule)] bg-[var(--surface)] px-4 py-2 text-sm text-[var(--text-sec)]">
            <AlertCircle className="h-4 w-4 flex-shrink-0 text-[var(--mustard)]" />
            <span>
              <strong className="text-[var(--text-pri)]">No readiness rules for ranking</strong>
              {' — items are checked against the every-item rules only.'}
            </span>
            <Link
              to="/prioritization/settings/readiness"
              className="ml-auto flex items-center gap-1 font-medium text-[var(--text-pri)] underline"
            >
              Configure readiness
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}

        {/* Summary strip */}
        {activeItems.length > 0 && (
          <div className="mt-3 flex gap-4 border border-[var(--rule)] bg-[var(--surface)] px-4 py-2 text-sm">
            <span className="flex items-center gap-1.5 text-[var(--green)] dark:text-[var(--text-ter)]">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <strong>{readyCount}</strong> ready
            </span>
            <span className="flex items-center gap-1.5 text-[var(--coral)]">
              <XCircle className="h-3.5 w-3.5" />
              <strong>{notReadyCount}</strong> not ready
            </span>
            <span className="text-[var(--text-ter)]">
              {scopeItemIds
                ? `${activeItems.length} of ${allActiveCount} active items`
                : `${activeItems.length} active items total`}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      {activeItems.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-8 text-center">
          <div>
            <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-[var(--text-ter)]" />
            <p className="text-sm font-medium text-[var(--text-ter)]">
              {scopeItemIds ? 'No items here yet' : 'No active items'}
            </p>
            <p className="mt-1 text-xs text-[var(--text-ter)]">
              {scopeItemIds
                ? `Add items to this ${selectedTrackId ? 'track' : 'cycle'}'s pool to see them here.`
                : 'Active items will appear here once created.'}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1">
          {/* Left: item list */}
          <div className="flex w-72 flex-shrink-0 flex-col border-r border-[var(--rule)] bg-[var(--bg-raised)]">
            <div className="border-b border-[var(--rule)] px-3 py-2">
              <div className="flex items-center gap-2 border border-[var(--rule)] bg-[var(--surface)] px-3 py-1.5">
                <Search className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search items…"
                  className="flex-1 bg-transparent text-sm focus:outline-none"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {displayItems.map((item) => {
                const result = evalResults[item.id];
                const isSelected = item.id === selectedItemId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedItemId(item.id)}
                    className={[
                      'flex w-full items-center gap-3 border-b border-[var(--rule)] px-4 py-3 text-left transition-colors',
                      isSelected ? 'bg-[var(--slate-dim)]' : 'hover:bg-[var(--surface)]',
                    ].join(' ')}
                  >
                    <div className="min-w-0 flex-1">
                      <p className={`truncate text-sm font-medium ${isSelected ? 'text-[var(--text-sec)]' : 'text-[var(--text-pri)]'}`}>
                        {item.title}
                      </p>
                    </div>
                    {result && (
                      <ReadinessPill
                        pass={result.pass}
                        passCount={result.passCount}
                        total={result.totalCount}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: item detail */}
          {selectedItem ? (
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              {/* Item header */}
              <div className="flex-shrink-0 border-b border-[var(--rule)] bg-[var(--bg-raised)] px-6 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold text-[var(--text-pri)]">
                      {selectedItem.title}
                    </h2>
                  </div>
                  <Link
                    to={`/prioritization/items/${selectedItem.id}`}
                    className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:underline"
                  >
                    Open item
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </div>

                {/* Criteria summary for this item */}
                {selectedItemResult && (
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => setShowCriteriaDetail((v) => !v)}
                      className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                    >
                      <ChevronRight
                        className={`h-3.5 w-3.5 transition-transform ${showCriteriaDetail ? 'rotate-90' : ''}`}
                      />
                      {selectedItemResult.pass ? (
                        <span className="font-medium text-[var(--green)] dark:text-[var(--text-ter)]">All criteria met</span>
                      ) : (
                        <span className="font-medium text-[var(--coral)]">
                          {selectedItemResult.totalCount - selectedItemResult.passCount} criteria not met
                        </span>
                      )}
                    </button>

                    {showCriteriaDetail && (
                      <div className="mt-2 space-y-1.5">
                        {selectedItemResult.criteria.map((r, i) => (
                          <CriterionResultRow
                            key={i}
                            criterion={r.criterion}
                            pass={r.pass}
                            reason={r.reason}
                            remediationRole={r.remediationRole}
                            remediationType={r.remediationType}
                            everyItem={r.everyItem}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Values tab */}
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <ValuesTab item={selectedItem} userId={userId ?? ''} />
              </div>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center text-[var(--text-ter)]">
              <p className="text-sm">Select an item to see its values</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
